<template>
    <highcharts class="hc" :ref="refId" :options="options" style="max-height: 500px"></highcharts>
</template>

<script>
    // utils
    import {getKey} from "../../utils/common";
    // constants
    import {chartColors} from "../../constants/chart-constants";
    import {iconsUnits} from "../../constants/sensors-constants";
    // external
    import Highcharts from 'highcharts';
    import exportingInit from 'highcharts/modules/exporting';
    import exportingData from 'highcharts/modules/export-data';
    // mixins
    import computePlotLinesMixin from "../../mixins/computePlotLinesMixin";
    import ChartZoomControlMixin from "../../mixins/ChartZoomControlMixin";
    import ChartOptionsMixin from "../../mixins/ChartOptionsMixin";

    Highcharts.setOptions({
        colors: chartColors
    });

    exportingInit(Highcharts);
    exportingData(Highcharts);
    /**
     * Wrapper for highcharts component.
     * Has dynamic ref key and zoom reset handler.
     * Initializes localization for the chart and
     * holds the options for SPLINE type charts.
     */

    export default {
        name: "SplineChartDouble",
        mixins: [computePlotLinesMixin, ChartZoomControlMixin, ChartOptionsMixin],
        props: {
            zoomKey: {
                type: Number,
                required: true
            },
            data: {
                type: Object,
                required: true
            },
            metric: {
                type: String
            },
            showPlotLine: {
                type: Boolean
            }
        },
        created() {
            Highcharts.setOptions({
                lang: this.getChartTranslations()
            });
        },
        computed: {
            /**
             * Get a dynamic key for referencing the highcharts object.
             * NOTE: zoom level is controlled viz the zoomKey and this.
             */
            refId() {
                return getKey();
            },
            options() {
                let options = {
                    chart: {
                        zoomType: 'x',
                        type: 'spline',
                        inverted: false
                    },
                    exporting: this.getChartExportingOptions(),
                    resetZoomButton: {
                        theme: {
                            display: 'none'
                        }
                    },
                    title: {
                        text: null
                    },
                    xAxis: {
                        reversed: false,
                        type: 'datetime',
                        title: {
                            text: '',
                            style: {
                                color: "#6299EB"
                            }
                        },
                        startOnTick: true,
                        endOnTick: true,
                        min: this.data.startDate,
                        max: this.data.endDate
                    },
                    yAxis: this.getYAxis(),
                    tooltip: {
                        shared: true
                    },
                    legend: {
                        enabled: true
                    },
                    credits: {
                        enabled: false
                    }
                };

                // update data by specifying the yAxis
                this.updateSeriesWithAxes();
                // add data
                options.series = this.data.series;
                // add possible plot lines
                if (this.showPlotLine) {
                    options.xAxis.plotLines = this.computePlotLines(this.data.plotLines.xAxis);
                    options.yAxis.plotLines = this.computePlotLines(this.data.plotLines.yAxis);
                }
                return options;
            }
        },
        methods: {
            /**
             * Force insert for which axis this seriesItem is.
             * Goal is to map the seriesItem to an axis as to be able
             * to scale both in the same way on the screen.
             */
            updateSeriesWithAxes() {
                for (let index = 0; index < this.data.series.length; index++) {
                    this.data.series[index].yAxis = index;
                }
            },
            /**
             * For each seriesItem in teh data series, generate
             * a yAxis map with options.
             * @returns {[]}
             */
            getYAxis() {
                let resultAxis = [];
                let opposite = false;

                // Inspect data and try to get the series names and convert to labels
                for (const seriesItem of this.data.series) {

                    const seriesNameToUnit = iconsUnits?.[seriesItem.originalName]?.unit || '';
                    let localizedName = '';

                    if (this.$i18n.te(seriesNameToUnit)) {
                        localizedName = this.$i18n.t(seriesNameToUnit);
                    } else {
                        localizedName = this.$i18n.t(this.metric || 'sensors-page.y-label');
                    }

                    resultAxis.push({
                        title: {
                            text: localizedName,
                            style: {
                                color: "#6299EB"
                            }
                        },
                        labels: {
                            x: 0,
                            y: -2
                        },
                        opposite: opposite
                    });

                    // should switch to the other side
                    opposite = !opposite;
                }

                return resultAxis;
            }
        }
    };
</script>
